import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TimeCalculator.module.css';

const TimeCalculator = () => {
  const { t } = useTranslation();
  const [articlesPerWeek, setArticlesPerWeek] = useState(1);

  // Calculs basés sur les économies de temps
  const TIME_SAVED_PER_ARTICLE = 0.7 / 1; // 0.7 heures pour 1 article par semaine par mois
  const HOURLY_VALUE = 10; // Valeur horaire en euros

  const hoursPerMonth = (articlesPerWeek * TIME_SAVED_PER_ARTICLE).toFixed(1);
  const hoursPerYear = (articlesPerWeek * TIME_SAVED_PER_ARTICLE * 12).toFixed(0);
  const annualValue = Math.round(parseFloat(hoursPerYear) * HOURLY_VALUE);
  const articlesPerYear = articlesPerWeek * 52;

  // Déterminer le message en fonction des heures économisées par an
  const getMotivationMessage = () => {
    const hours = parseFloat(hoursPerYear);
    
    if (hours < 35) {
      return {
        title: t('home.calculator.automation.title_days', 'Vous gagnez plusieurs jours de travail par an'),
        description: t('home.calculator.automation.description', 
          'Imaginez ce que vous pourriez faire avec tout ce temps libre. Sourcer plus de produits, développer de nouveaux canaux, ou simplement profiter de votre vie.'
        )
      };
    } else if (hours < 70) {
      return {
        title: t('home.calculator.automation.title_week', 'Vous gagnez presque une semaine de travail par an'),
        description: t('home.calculator.automation.description', 
          'Imaginez ce que vous pourriez faire avec tout ce temps libre. Sourcer plus de produits, développer de nouveaux canaux, ou simplement profiter de votre vie.'
        )
      };
    } else if (hours < 140) {
      return {
        title: t('home.calculator.automation.title_weeks', 'Vous gagnez plusieurs semaines de travail par an !'),
        description: t('home.calculator.automation.description', 
          'Imaginez ce que vous pourriez faire avec tout ce temps libre. Sourcer plus de produits, développer de nouveaux canaux, ou simplement profiter de votre vie.'
        )
      };
    } else if (hours < 280) {
      return {
        title: t('home.calculator.automation.title_month', 'Vous gagnez plus d\'un mois de travail par an !'),
        description: t('home.calculator.automation.description_intense', 
          'C\'est énorme ! Avec tout ce temps libre, vous pourriez transformer complètement votre business, développer de nouvelles activités, ou profiter d\'un vrai équilibre vie pro/perso.'
        )
      };
    } else {
      return {
        title: t('home.calculator.automation.title_months', 'Vous gagnez plusieurs mois de travail par an !'),
        description: t('home.calculator.automation.description_extreme', 
          'Impressionnant ! Vous pourriez pratiquement doubler votre activité, créer une nouvelle source de revenus, ou enfin prendre ces longues vacances que vous méritez.'
        )
      };
    }
  };

  const motivationMessage = getMotivationMessage();

  const handleSliderChange = (e) => {
    setArticlesPerWeek(parseInt(e.target.value));
  };

  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.sliderSection}>
        <div className={styles.sliderHeader}>
          <span className={styles.sliderLabel}>
            {t('home.calculator.selling', 'Je vends')}{' '}
            <span className={styles.highlight}>{articlesPerWeek}</span>{' '}
            {t('home.calculator.articlesPerWeek', articlesPerWeek > 1 ? 'articles par semaine' : 'article par semaine')}
          </span>
        </div>
        <div className={styles.sliderContainer}>
          <input
            type="range"
            min="1"
            max="100"
            value={articlesPerWeek}
            onChange={handleSliderChange}
            className={styles.slider}
          />
          <div className={styles.sliderLabels}>
            <span>1 {t('home.calculator.article', 'article')}</span>
            <span>100 {t('home.calculator.articles', 'articles')}</span>
          </div>
        </div>
      </div>

      <div className={styles.resultsSection}>
        <div className={styles.mainResult}>
          <p className={styles.resultLabel}>
            {t('home.calculator.stoflowSaves', 'StoFlow vous rend')}
          </p>
          <h2 className={styles.hoursDisplay}>
            {hoursPerMonth} {t('home.calculator.hours', 'heures')}
          </h2>
          <p className={styles.resultSubLabel}>
            {t('home.calculator.perMonth', 'de votre vie par mois')}
          </p>
        </div>

        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{hoursPerYear}h</div>
            <div className={styles.statLabel}>{t('home.calculator.perYear', 'Par an')}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{annualValue}€</div>
            <div className={styles.statLabel}>{t('home.calculator.annualValue', 'Valeur annuelle')}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{articlesPerYear}</div>
            <div className={styles.statLabel}>{t('home.calculator.articlesPerYear', 'Articles/an')}</div>
          </div>
        </div>

        <div className={styles.motivationBox}>
          <div className={styles.motivationIcon}>📈</div>
          <div className={styles.motivationContent}>
            <h4>{motivationMessage.title}</h4>
            <p>{motivationMessage.description}</p>
          </div>
        </div>

        <div className={styles.calculationDetails}>
          <h4>{t('home.calculator.whyRealistic', 'Pourquoi ce calcul est réaliste :')}</h4>
          <ul className={styles.detailsList}>
            <li>
              <span className={styles.detailIcon}>⏱️</span>
              <span>
                <strong>{t('home.calculator.detour', 'Détourage :')}</strong>{' '}
                {t('home.calculator.detourDetails', '3-5 min manuellement vs. 0.2s avec StoFlow')}
              </span>
            </li>
            <li>
              <span className={styles.detailIcon}>✍️</span>
              <span>
                <strong>{t('home.calculator.description', 'Description :')}</strong>{' '}
                {t('home.calculator.descriptionDetails', '2-3 min de rédaction vs. 3s d\'IA')}
              </span>
            </li>
            <li>
              <span className={styles.detailIcon}>📤</span>
              <span>
                <strong>{t('home.calculator.publication', 'Publication :')}</strong>{' '}
                {t('home.calculator.publicationDetails', 'Multi-saisie évitée = 5 min économisées')}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TimeCalculator;
